import React, { useState, useEffect } from 'react';
import { updateTimer } from '../services/vpsApi';
import { useLanguage } from '../contexts/LanguageContext';
import './TimerControls.css';

function TimerControls({ timer, onClose, standalone = false }) {
  const { t } = useLanguage();
  const [jumpToLevel, setJumpToLevel] = useState('');
  const [bubbleSingleHandTime, setBubbleSingleHandTime] = useState('');
  const [bubbleMultiHandTime, setBubbleMultiHandTime] = useState('');
  const [bubbleHandCount, setBubbleHandCount] = useState('');
  const [previousBubbleState, setPreviousBubbleState] = useState(null);

  // Check if bubble time is active
  const isBubbleTimeActive = () => {
    if (!timer) return false;

    const raceCategory = timer.raceCategory;
    const tableCount = parseInt(timer.tableCount);
    const raceStatus = timer.raceStatus;

    // Bubble time conditions:
    // 1. 賽事類別 = 錦標賽
    // 2. 賽事狀態 = 泡泡時間
    // 3. 桌數 > 1
    return raceCategory === '錦標賽' &&
           raceStatus === '泡泡時間' &&
           !isNaN(tableCount) &&
           tableCount > 1;
  };

  // 監控泡泡時間狀態變化，進入泡泡時間時自動重整頁面
  useEffect(() => {
    const currentBubbleState = isBubbleTimeActive();

    // 只在狀態真正從 false 變成 true 時重整（null 代表初始狀態，不觸發重整）
    if (previousBubbleState === false && currentBubbleState === true) {
      console.log('Entering bubble time, reloading page...');
      window.location.reload();
    }

    // 只在初始化後才更新 previousBubbleState
    if (previousBubbleState !== null || currentBubbleState !== null) {
      setPreviousBubbleState(currentBubbleState);
    }
  }, [timer?.raceStatus, timer?.raceCategory, timer?.tableCount]);

  // Calculate current elapsed time
  const getCurrentElapsed = () => {
    if (!timer.startTime) return 0;
    const startTime = new Date(timer.startTime);
    const now = new Date();
    return Math.floor((now - startTime) / 1000);
  };

  const totalDuration = timer.segments ? timer.segments.reduce((sum, seg) => sum + seg.duration, 0) : 0;
  const currentElapsed = getCurrentElapsed();

  // 計算目前區段
  const getCurrentSegmentInfo = () => {
    if (!timer.segments || timer.segments.length === 0) return null;

    let cumulativeTime = 0;
    for (let i = 0; i < timer.segments.length; i++) {
      const segment = timer.segments[i];
      const segmentStart = cumulativeTime;
      const segmentEnd = cumulativeTime + segment.duration;

      if (currentElapsed >= segmentStart && currentElapsed < segmentEnd) {
        return {
          segment: segment,
          index: i,
          duration: segment.duration,
          elapsed: currentElapsed - segmentStart,
          remaining: segmentEnd - currentElapsed,
          segmentStart: segmentStart
        };
      }

      cumulativeTime = segmentEnd;
    }

    // 如果超過所有區段，返回最後一個區段
    const lastSegment = timer.segments[timer.segments.length - 1];
    const lastSegmentStart = cumulativeTime - lastSegment.duration;
    return {
      segment: lastSegment,
      index: timer.segments.length - 1,
      duration: lastSegment.duration,
      elapsed: lastSegment.duration,
      remaining: 0,
      segmentStart: lastSegmentStart
    };
  };

  const currentSegmentInfo = getCurrentSegmentInfo();

  // 時間軸顯示目前區段的進度
  const [timelineValue, setTimelineValue] = useState(currentSegmentInfo?.elapsed || 0);
  const [isAdjustingTimeline, setIsAdjustingTimeline] = useState(false);

  // Update timeline value when current segment elapsed changes (only if not manually adjusting)
  useEffect(() => {
    if (currentSegmentInfo && !isAdjustingTimeline) {
      setTimelineValue(currentSegmentInfo.elapsed);
    }
  }, [currentSegmentInfo?.elapsed, isAdjustingTimeline]);

  // Format time for display
  const formatDuration = (seconds) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    if (hours > 0) {
      return `${hours}:${String(minutes).padStart(2, '0')}:${String(seconds % 60).padStart(2, '0')}`;
    }
    return `${minutes}:${String(seconds % 60).padStart(2, '0')}`;
  };

  // 基本控制函數
  const handleStart = async () => {
    try {
      await updateTimer(timer.id, {
        status: 'active',
        startTime: new Date().toISOString()
      });
      console.log('計時器已啟動');
    } catch (err) {
      console.error('啟動失敗：', err);
      alert('啟動失敗：' + err.message);
    }
  };

  const handlePause = async () => {
    try {
      // 計算目前已經過的時間
      const currentElapsed = getCurrentElapsed();

      await updateTimer(timer.id, {
        status: 'paused',
        pausedAt: new Date().toISOString(),
        pausedElapsed: currentElapsed
      });
      console.log('計時器已暫停');
    } catch (err) {
      console.error('暫停失敗：', err);
      alert('暫停失敗：' + err.message);
    }
  };

  const handleResume = async () => {
    try {
      // 從暫停點繼續：計算新的 startTime
      const pausedElapsed = timer.pausedElapsed || 0;
      const now = new Date();
      const newStartTime = new Date(now.getTime() - (pausedElapsed * 1000));

      await updateTimer(timer.id, {
        status: 'active',
        startTime: newStartTime.toISOString(),
        pausedAt: null,
        pausedElapsed: null
      });
      console.log('計時器已繼續');
    } catch (err) {
      console.error('繼續失敗：', err);
      alert('繼續失敗：' + err.message);
    }
  };

  const handleStop = async () => {
    if (!window.confirm('確定要停止計時器嗎？計時器將回到待開始狀態。')) return;

    try {
      await updateTimer(timer.id, {
        status: 'pending',
        currentSegmentIndex: 0,
        elapsedSeconds: 0,
        segmentElapsed: 0,
        startTime: null,
        endTime: null
      });
      console.log('計時器已停止並重置');
    } catch (err) {
      console.error('停止失敗：', err);
      alert('停止失敗：' + err.message);
    }
  };

  const handleReset = async () => {
    if (!window.confirm('確定要重置計時器嗎？這將清除所有進度。')) return;

    try {
      await updateTimer(timer.id, {
        status: 'pending',
        startTime: null,
        endTime: null,
        currentSegmentIndex: 0
      });
      console.log('計時器已重置');
    } catch (err) {
      console.error('重置失敗：', err);
      alert('重置失敗：' + err.message);
    }
  };

  // 跳區間計時 - 重新計時該區間
  const handleJumpToLevel = async () => {
    if (!jumpToLevel) {
      alert('請選擇區段');
      return;
    }

    const levelIndex = parseInt(jumpToLevel);

    try {
      // Calculate total time elapsed up to the target level
      let elapsedToLevel = 0;
      for (let i = 0; i < levelIndex; i++) {
        elapsedToLevel += timer.segments[i].duration;
      }

      // Set startTime to make it appear at the beginning of the target level
      const now = new Date();
      const newStartTime = new Date(now.getTime() - (elapsedToLevel * 1000));

      await updateTimer(timer.id, {
        startTime: newStartTime.toISOString(),
        currentSegmentIndex: levelIndex,
        status: 'active' // Ensure timer is running
      });

      const segmentName = timer.segments[levelIndex]?.name || `區段 ${levelIndex + 1}`;
      alert(`已跳轉至 ${segmentName}，並重新計時`);
      setJumpToLevel('');
    } catch (err) {
      alert('跳轉失敗：' + err.message);
    }
  };

  // 時間軸調整
  const handleTimelineChange = (e) => {
    setIsAdjustingTimeline(true);
    setTimelineValue(parseInt(e.target.value));
  };

  const handleTimelineMouseUp = async () => {
    // Auto-apply when user releases the slider
    if (!timer.startTime || !currentSegmentInfo) {
      setIsAdjustingTimeline(false);
      return;
    }

    try {
      // 將目前區段的時間轉換為總經過時間
      const targetSegmentElapsed = timelineValue;
      const totalTargetElapsed = currentSegmentInfo.segmentStart + targetSegmentElapsed;

      const now = new Date();
      const newStartTime = new Date(now.getTime() - (totalTargetElapsed * 1000));

      await updateTimer(timer.id, {
        startTime: newStartTime.toISOString(),
        currentSegmentIndex: currentSegmentInfo.index
      });

      console.log('時間已調整');
    } catch (err) {
      console.error('調整失敗：', err);
      alert('調整失敗：' + err.message);
    } finally {
      // Re-enable auto-update after a short delay
      setTimeout(() => {
        setIsAdjustingTimeline(false);
      }, 1000);
    }
  };

  const handleTimelineApply = async () => {
    if (!timer.startTime || !currentSegmentInfo) {
      alert('計時器尚未啟動');
      return;
    }

    setIsAdjustingTimeline(true);

    try {
      // 將目前區段的時間轉換為總經過時間
      const targetSegmentElapsed = timelineValue;
      const totalTargetElapsed = currentSegmentInfo.segmentStart + targetSegmentElapsed;

      const now = new Date();
      const newStartTime = new Date(now.getTime() - (totalTargetElapsed * 1000));

      await updateTimer(timer.id, {
        startTime: newStartTime.toISOString(),
        currentSegmentIndex: currentSegmentInfo.index
      });

      console.log('時間已調整');
      alert('時間已套用');
    } catch (err) {
      console.error('調整失敗：', err);
      alert('調整失敗：' + err.message);
    } finally {
      // Re-enable auto-update after a short delay
      setTimeout(() => {
        setIsAdjustingTimeline(false);
      }, 1000);
    }
  };

  // 泡泡時間跳轉
  const handleBubbleTimeJump = async () => {
    if (!timer.startTime || !currentSegmentInfo) {
      alert(t('message.timerNotStarted', '計時器尚未啟動'));
      return;
    }

    let totalBubbleTime = 0;
    let displayMessage = '';

    // 根據模式計算跳轉時間
    if (timer.raceMode === '單手') {
      // 單手模式：只需要單手時間
      const handTime = parseInt(bubbleSingleHandTime);
      if (isNaN(handTime) || handTime <= 0) {
        alert('請輸入有效的單手時間');
        return;
      }
      totalBubbleTime = handTime;
      displayMessage = `已跳轉 ${handTime} 秒`;
    } else if (timer.raceMode === '多手') {
      // 多手模式：單手時間 × 手數
      const handTime = parseInt(bubbleMultiHandTime);
      const hands = parseInt(bubbleHandCount);

      if (isNaN(handTime) || handTime <= 0) {
        alert('請輸入有效的單手時間');
        return;
      }

      if (isNaN(hands) || hands <= 0) {
        alert('請輸入有效的手數');
        return;
      }

      totalBubbleTime = handTime * hands;
      displayMessage = `已跳轉 ${hands} 手（${totalBubbleTime} 秒）`;
    } else {
      alert('請在 Google Sheets 設定「模式」欄位為「單手」或「多手」');
      return;
    }

    try {
      // Get current total elapsed time
      const currentTotalElapsed = currentSegmentInfo.segmentStart + currentSegmentInfo.elapsed;

      // Add bubble time to current elapsed
      const newTotalElapsed = currentTotalElapsed + totalBubbleTime;

      // Calculate new start time
      const now = new Date();
      const newStartTime = new Date(now.getTime() - (newTotalElapsed * 1000));

      // Update timer
      await updateTimer(timer.id, {
        startTime: newStartTime.toISOString()
      });

      alert(displayMessage);
      setBubbleHandCount('');
      setBubbleSingleHandTime('');
      setBubbleMultiHandTime('');
    } catch (err) {
      alert('跳轉失敗：' + err.message);
    }
  };

  const containerClass = standalone ? "timer-controls-standalone" : "timer-controls-overlay";
  const contentClass = standalone ? "timer-controls-standalone-content" : "timer-controls-modal";

  return (
    <div className={containerClass} onClick={standalone ? undefined : onClose}>
      <div className={contentClass} onClick={standalone ? undefined : (e) => e.stopPropagation()}>
        {!standalone && (
          <div className="modal-header">
            <h2>{t('control.title', '計時器控制面板')}</h2>
            <button className="close-btn" onClick={onClose}>✕</button>
          </div>
        )}

        <div className="modal-body">
          {/* 基本控制 */}
          <div className="control-section">
            <h3>{t('control.basicControl', '基本控制')}</h3>
            <div className="control-inputs">
              {timer.status === 'pending' && (
                <button className="btn btn-success" onClick={handleStart}>
                  ▶️ {t('ui.start', '開始')}
                </button>
              )}
              {timer.status === 'active' && !isBubbleTimeActive() && (
                <button className="btn btn-warning" onClick={handlePause}>
                  ⏸️ {t('ui.pause', '暫停')}
                </button>
              )}
              {timer.status === 'paused' && !isBubbleTimeActive() && (
                <button className="btn btn-success" onClick={handleResume}>
                  ▶️ {t('ui.resume', '繼續')}
                </button>
              )}
              {(timer.status === 'active' || timer.status === 'paused') && (
                <button className="btn btn-danger" onClick={handleStop}>
                  ⏹️ {t('ui.stop', '停止')}
                </button>
              )}
              {(timer.status === 'stopped' || timer.status === 'completed') && (
                <button className="btn btn-info" onClick={handleReset}>
                  🔄 {t('ui.reset', '重置')}
                </button>
              )}
              {timer.refreshLink && (
                <button
                  className="btn btn-warning"
                  onClick={async () => {
                    try {
                      const response = await fetch(timer.refreshLink);
                      if (response.ok) {
                        alert('✅ 已發送刷新信號至所有顯示頁面');
                      } else {
                        alert('❌ 刷新失敗');
                      }
                    } catch (error) {
                      console.error('刷新錯誤:', error);
                      alert('❌ 刷新失敗：' + error.message);
                    }
                  }}
                  title="強制刷新所有顯示此計時器的頁面"
                >
                  🔄 強制刷新
                </button>
              )}
            </div>
          </div>

          {/* 跳區間計時 */}
          <div className="control-section">
            <h3>{t('control.jumpLevel', '跳區間計時')} ({t('control.jumpLevelDesc', '重新計時')})</h3>
            <div className="control-inputs">
              <select
                className="level-select"
                value={jumpToLevel}
                onChange={(e) => setJumpToLevel(e.target.value)}
              >
                <option value="">{t('control.selectSegment', '選擇區段')}...</option>
                {timer.segments?.map((segment, idx) => (
                  <option key={idx} value={idx}>
                    {segment.name}
                  </option>
                ))}
              </select>
              <button className="btn btn-warning" onClick={handleJumpToLevel}>
                {t('control.jump', '跳轉')}
              </button>
            </div>
          </div>

          {/* 時間軸調整 */}
          <div className="control-section">
            <h3>{t('control.timeline', '時間軸調整')}</h3>
            <div className="timeline-control">
              <input
                type="range"
                min="0"
                max={currentSegmentInfo?.duration || 0}
                value={timelineValue}
                onChange={handleTimelineChange}
                onMouseUp={handleTimelineMouseUp}
                onTouchEnd={handleTimelineMouseUp}
                className="timeline-slider"
              />
              <div className="timeline-info">
                <span>{t('control.timelineProgress', '目前區段進度')}：{formatDuration(timelineValue)} / {formatDuration(currentSegmentInfo?.duration || 0)}</span>
              </div>
              <div className="timeline-debug" style={{ fontSize: '12px', color: '#64748b', marginTop: '8px' }}>
                <div>{t('control.currentSegment', '目前區段')}: {currentSegmentInfo?.segment?.name || '-'}</div>
                <div>{t('control.segmentDuration', '區段時長')}: {currentSegmentInfo ? Math.floor(currentSegmentInfo.duration / 60) : 0} {t('common.minutes', '分')}</div>
              </div>
              <button className="btn btn-success" onClick={handleTimelineApply}>
                {t('control.manualApply', '手動套用')}
              </button>
            </div>
          </div>

          {/* 泡泡時間 */}
          <div className="control-section bubble-time-section">
            <h3>{t('control.bubbleTime', '泡泡時間')}</h3>

            {isBubbleTimeActive() ? (
              <div className="bubble-active">
                <p>✅ {t('control.bubbleTimeActive', '泡泡時間進行中')}</p>

                {/* 根據模式顯示不同的輸入欄位 */}
                {timer.raceMode === '單手' ? (
                  /* 單手模式 */
                  <div className="control-inputs">
                    <label>{t('control.bubbleSingleHand', '單手時間')} ({t('common.seconds', '秒')})</label>
                    <input
                      type="number"
                      placeholder={t('control.bubbleSingleHand', '單手時間')}
                      value={bubbleSingleHandTime}
                      onChange={(e) => setBubbleSingleHandTime(e.target.value)}
                      min="1"
                    />
                    <button className="btn btn-primary" onClick={handleBubbleTimeJump}>
                      {t('control.execute', '執行')}
                    </button>
                  </div>
                ) : timer.raceMode === '多手' ? (
                  /* 多手模式 */
                  <div className="control-inputs">
                    <label>{t('control.bubbleSingleHand', '單手時間')} ({t('common.seconds', '秒')})</label>
                    <input
                      type="number"
                      placeholder={t('control.bubbleSingleHand', '單手時間')}
                      value={bubbleMultiHandTime}
                      onChange={(e) => setBubbleMultiHandTime(e.target.value)}
                      min="1"
                    />
                    <label>{t('control.bubbleHandCount', '手數')}</label>
                    <input
                      type="number"
                      placeholder={t('control.bubbleHandCount', '手數')}
                      value={bubbleHandCount}
                      onChange={(e) => setBubbleHandCount(e.target.value)}
                      min="1"
                    />
                    <button className="btn btn-primary" onClick={handleBubbleTimeJump}>
                      {t('control.execute', '執行')}
                    </button>
                  </div>
                ) : (
                  /* 未設定模式或其他模式 */
                  <p className="bubble-hint">請在 Google Sheets 的「模式」欄位設定為「單手」或「多手」</p>
                )}
              </div>
            ) : (
              <div className="bubble-inactive">
                <p>待啟動</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default TimerControls;
